import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { checkIsDemoOrg } from 'src/app/core/models/organizations';

// === Dashboard Interfaces ===

/** Bundles score + exposure because both come from the same OpenSearch data pass */
export interface ItdrScoreResponse {
  identity_score: IdentityScoreData;
  exposure_metrics: ExposureMetrics;
}

export interface ExecutiveMetrics {
  high_risk_identities: number;
  privileged_accounts_at_risk: number;
  /**
   * Count of identities with a `leakedCredentials` riskDetection whose
   * `detectedDateTime` is newer than the user's `lastPasswordChangeDateTime`
   * (i.e., the credential is leaked AND has not been rotated since).
   * Sourced from MS Graph Identity Protection joined to /users in the UDP pipeline.
   */
  leaked_credentials: number;
  /**
   * Availability of the leaked-credentials count:
   * - 'available'   : a count we can trust
   * - 'unavailable' : the tenant's tier does not include the signal
   *
   * There is no 'detection_disabled'. Microsoft does not expose whether a tenant switched
   * leaked-credential detection off, so that state could never be set truthfully.
   *
   * `tenant_count` is gone too: it was declared here and read by nothing.
   */
  leaked_credentials_source: 'available' | 'unavailable';
  users_monitored: number;
  /** Enabled, non-guest accounts: the Core subset of `users_monitored`. */
  core_identities: number;
}

export interface ExposureMetrics {
  users_exposed: number;
  /** Dynamic summary strings derived from failing score checks */
  misconfig_summary: string[];
}

export interface DetectionTrendData {
  labels: string[];
  values: number[];
}

/** Identity integrations the ITDR feature needs to function. */
export type ItdrIntegrationKey = 'entraId' | 'm365';

/** Freshness of the identity-provider data, shown page-wide (applies to both tabs). */
export interface DataSyncStatus {
  /** ISO 8601 timestamp of the last successful identity-provider data pull. */
  last_synced_at: string;
  /** Identity source the data was pulled from, e.g. "Microsoft Entra ID". */
  source: string;
  /** Whether the customer's Microsoft Entra ID integration is configured. */
  has_entra_id_integration: boolean;
  /** Whether the customer's Microsoft 365 integration is configured. (Required in the near future.) */
  has_m365_integration: boolean;
  /** Whether the first identity-provider data pull has completed. False during onboarding. */
  initial_sync_complete: boolean;
  /** Integrations the customer still needs to configure before ITDR works. */
  missing_integrations: ItdrIntegrationKey[];
}

export type ScoreFactorStatus = 'pass' | 'fail' | 'insufficient_data';
export type ScoreSeverityTier = 'Critical' | 'High' | 'Medium' | 'Low';
export type ScoreFactorCategory = 'Authentication' | 'Privilege' | 'Policy' | 'Behavioral';
/**
 * How a check is evaluated and remediated:
 * - 'entity': produces a list of failing identities/objects (per-entity accept, linked cases).
 * - 'tenant': a single tenant-wide configuration setting (check-level accept, no entity list).
 */
export type ScoreCheckScope = 'entity' | 'tenant';

export interface IdentityScoreData {
  current_score: number;
  previous_score: number;
  score_trend: 'up' | 'down' | 'stable';
  total_checks: number;
  passing_checks: number;
  failing_checks: number;
  accepted_checks: number;
  disabled_checks: number;
  /**
   * Relative severity weights (ratio only, NOT fixed point deductions).
   * The score normalizes against the sum of evaluated weights, so the catalog always totals 100%.
   */
  tier_weights: Record<ScoreSeverityTier, number>;
  /** Sum of weights of all evaluated (pass/fail) checks. The denominator behind score_impact. */
  total_weight: number;
  factors: ScoreFactor[];
}

export interface ScoreFactor {
  check_id: string;
  name: string;
  /**
   * What the risk is, from the catalog — the same sentence for every customer.
   * Distinct from `detail`, which is this tenant's current state.
   */
  description: string;
  status: ScoreFactorStatus;
  /** Relative weight derived from the effective severity tier (15/10/5/2). 0 when severity is null. */
  weight: number;
  /**
   * Percentage points this check costs the score when failing = round(100 * weight / total_weight).
   * 0 when passing, accepted, or insufficient_data.
   */
  score_impact: number;
  category: ScoreFactorCategory;
  severity: ScoreSeverityTier | null;
  /** Entity-scoped (per-identity list) or tenant-scoped (single config setting). */
  scope: ScoreCheckScope;
  /** CIS Controls v8 reference (data/docs only, not rendered in the customer UI). */
  cis_control: string;
  /**
   * Threat class from the catalog, rendered as "Could Enable" in the score dialog.
   * A different catalog column from `attack_path`, and the two disagree on 5 of the
   * 10 checks, so neither can stand in for the other.
   */
  detection_type?: string;
  /** Attack path this check guards against (e.g., "Account Takeover") */
  attack_path?: string;
  exploitation_likelihood?: 'High' | 'Medium' | 'Low';
  /** One-line customer-facing explanation of the current state. */
  detail: string;
  /** Number of affected entities (users/apps), shown in the drill-down. Display only, does not affect score. */
  failed_entity_count?: number;
  total_entity_count?: number;
  /** Whether the customer has accepted the risk (failing but no score impact applied) */
  accepted: boolean;
  /** Whether the customer overrode the default severity tier (future per-check tier reassignment) */
  is_overridden: boolean;
  /** System default severity for this check (used to display override hint) */
  default_severity: ScoreSeverityTier;
  /**
   * Which licence would make this check evaluable, when the tier is the reason.
   * Empty when the check was skipped because the read failed — most of them are.
   */
  required_license: string;
}

export interface PrivilegedAccessSummary {
  global_admins: number;
  recommended_max_admins: number;
  accounts_can_create_tokens: number;
  accounts_can_reset_passwords: number;
  accounts_can_grant_permissions: number;
}

/** The top 5 identities escalated in the last 30 days that fail a posture check. */
export interface IdentityRiskSummary {
  identity: string;
  /** The `app_id` for an app, the lowercase UPN for a user. Different apps can share a name. */
  identity_id: string;
  identity_type: 'User' | 'Service Account';
  /** Failing posture checks for this identity (current state) */
  misconfigs: IdentityMisconfig[];
  /** ITDR-related escalation count in the last 30 days */
  escalation_count_30d: number;
  /** Alert categories seen in those escalations */
  alert_categories: string[];
}

export interface IdentityMisconfig {
  /** Maps to a ScoreFactor check_id (e.g., "ITDR-MFA-001") */
  check_id: string;
  /** Human-readable name (e.g., "MFA Enrollment Gap") */
  check_name: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
}

// === Posture Interfaces ===

export type PostureRiskState = 'Open' | 'Accepted';

export interface PostureRiskCheck {
  check_id: string;
  title: string;
  description: string;
  category: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  /** Entity-scoped (per-identity list) or tenant-scoped (single config setting). */
  scope: ScoreCheckScope;
  /** CIS Controls v8 reference (data/docs only, not rendered in the customer UI). */
  cis_control?: string;
  /** Risk-acceptance decision. Separate from `status`: a check can be failing and accepted. */
  state: PostureRiskState;
  /**
   * Whether the check could be evaluated, and how it came out.
   *
   * 'insufficient_data' is not an edge case: 39 of 40 production organizations have at
   * least one check in it. The card must branch on this before reading the counters,
   * because a tenant-scoped check that could not be read still reports 1 / 1, which a
   * counter comparison renders as "Configured" — asserting a tenant is set up correctly
   * when nobody could read its settings.
   */
  status: ScoreFactorStatus;
  /** One-line reason for the current state; for 'insufficient_data', what could not be read. */
  detail: string;
  /** 'P1' or 'P2' when the tier is why the check was skipped; empty when the read simply failed. */
  required_license: string;
  /** What the check counts: 'user', 'application', 'service_principal' or 'tenant'. */
  target_type: string;
  total_entities: number;
  protected_entities: number;
  /**
   * How many entities fail. A count, not a list: the affected identities are served by
   * the paginated affected-users endpoint, and the worst single check in production has
   * over six thousand of them.
   */
  failed_entity_count: number;
  linked_case_count: number;
  impact_percentage: number;
  explanation: string;
  /**
   * Single canonical detection this check maps to (1:1 for MVP).
   * Absent for healthy/passing checks with no associated detection.
   */
  detection_type?: string;
  /**
   * Aggregate, non-sensitive current configuration shown in the expanded card.
   * Tenant config or aggregate posture only — never per-identity / PII values.
   */
  current_settings?: PostureCurrentSetting[];
  linked_cases: PostureLinkedCase[];
}

export interface PostureCurrentSetting {
  label: string;
  value: string;
}

/** One affected identity row for the paginated affected-users table. */
export interface AffectedUserRow {
  /**
   * The provider's immutable id for this entity, and what an acceptance is keyed on.
   *
   * Not `email`: a display name does not identify one entity — four organizations in
   * dev each have two distinct applications called "Zoho CRM" failing the same check —
   * and a user principal name can change, which would silently reopen an accepted risk
   * or follow the address to whoever inherits it.
   */
  entity_uid: string;
  /** What the row shows: an address for users, an application name for app checks. */
  email: string;
  status: PostureRiskState;
  /** Audit note captured when the risk was accepted (empty when Open). */
  reason: string;
  /** ISO 8601 timestamp the risk was accepted (empty when Open). */
  accepted_at: string;
}

/** Server-side page of affected identities, in the shape the shared grid reads. */
export interface AffectedUsersResponse {
  draw: number;
  recordsTotal: number;
  recordsFiltered: number;
  data: AffectedUserRow[];
  /** Summary counts read via the grid's successCallback to drive the card header. */
  total_count: number;
  accepted_count: number;
  open_count: number;
}

export interface PostureLinkedCase {
  request_id: string;
  created_time: string;
  ticket_status: string | null;
  subject: string;
  priority: string;
  ticket_closed_time: string;
  customer_response_time: string;
  ticket_source: string;
  issue_type: string;
  customer_defined_status: string;
  description: string;
  source_ip_address_hostname_mac: string;
  destination_ip_address_hostname_mac: string;
  affected_user: string;
}

// === Service ===

/**
 * ITDR API Service
 *
 * MVP: All endpoints use fixed lookback periods (30 days) or current snapshots.
 *
 * FUTURE: The following endpoints should accept optional `start_date` / `end_date`
 * query params to support user-selectable time ranges via the analytics-filter component:
 *   - GET /v1/itdr/detection-trend/{org}/?start_date=&end_date=
 *   - GET /v1/itdr/identity-risks/{org}/?start_date=&end_date=
 *   - GET /v1/itdr/executive-metrics/?start_date=&end_date= (if trend comparisons are re-added)
 *
 * Endpoints that remain current-snapshot regardless of time range:
 *   - GET /v1/itdr/score/              (live Entra posture checks)
 *   - GET /v1/itdr/privileged-access/  (live Entra role assignments)
 *   - GET /v1/itdr/posture/risk-checks/ (live posture checks + linked cases)
 */
@Injectable({
  providedIn: 'root',
})
export class ItdrService {
  private http = inject(HttpClient);
  private liveApiUrl = `${environment.apiUrl}/v1/itdr`;

  /**
   * Reads go through /demo for a demo org — same trick the analytics dashboards use.
   * Writes stay on the live API: DemoPage is a canned GET, it cannot persist an
   * acceptance or a severity override.
   */
  private get readApiUrl(): string {
    const demostr = checkIsDemoOrg() ? '/demo' : '';
    return `${environment.apiUrl}${demostr}/v1/itdr`;
  }

  /**
   * Freshness of the identity data, and the customer state the page switches on.
   *
   * Page-level: this is what decides whether the customer sees the dashboard, the
   * setup screen or "first sync in progress", so it is fetched before anything else.
   */
  public getDataSyncStatus(organizationId: number): Observable<DataSyncStatus> {
    return this.http.get<DataSyncStatus>(`${this.readApiUrl}/data-sync/${organizationId}/`);
  }

  /** Identity score + exposure metrics (Gold `gold_identity_score` and `gold_identity_checks`). */
  public getScore(organizationId: number): Observable<ItdrScoreResponse> {
    return this.http.get<ItdrScoreResponse>(`${this.readApiUrl}/score/${organizationId}/`);
  }

  /** Executive KPI tiles — current snapshot (Gold `gold_identity_exec`). */
  public getExecutiveMetrics(organizationId: number): Observable<ExecutiveMetrics> {
    return this.http.get<ExecutiveMetrics>(
      `${this.readApiUrl}/executive-metrics/${organizationId}/`
    );
  }

  /** Detection trend — fixed 30-day lookback (Twister alerts). */
  public getDetectionTrend(organizationId: number): Observable<DetectionTrendData> {
    return this.http.get<DetectionTrendData>(
      `${this.readApiUrl}/detection-trend/${organizationId}/`
    );
  }

  /**
   * Privileged access summary (Gold `gold_identity_privileged`).
   *
   * The organization is in the path, the way every other widget in the product
   * identifies its organization — the backend needs it to authorize the request, and a
   * user can belong to more than one, so it cannot be inferred from the session.
   */
  public getPrivilegedAccess(organizationId: number): Observable<PrivilegedAccessSummary> {
    return this.http.get<PrivilegedAccessSummary>(
      `${this.readApiUrl}/privileged-access/${organizationId}/`
    );
  }

  /** Misconfigured identities with escalation activity — fixed 30-day lookback (Gold posture + Twister escalations). */
  public getIdentityRisks(organizationId: number): Observable<IdentityRiskSummary[]> {
    return this.http.get<IdentityRiskSummary[]>(
      `${this.readApiUrl}/identity-risks/${organizationId}/`
    );
  }

  /** Posture risk checks — the ten cards on the posture tab (Gold `gold_identity_checks`). */
  public getPostureRiskChecks(organizationId: number): Observable<PostureRiskCheck[]> {
    return this.http.get<PostureRiskCheck[]>(
      `${this.readApiUrl}/posture/risk-checks/${organizationId}/`
    );
  }

  /**
   * Server-side paginated endpoint URL for a single check's affected identities.
   * Passed to <app-datatable [data-href]> so the grid issues its own page/sort/search
   * requests (PostgreSQL per-check entity list with acceptance state).
   */
  public getAffectedUsersUrl(checkId: string, organizationId: number): string {
    return `${this.readApiUrl}/posture/risk-checks/${checkId}/affected-users/${organizationId}/`;
  }

  /** Drilldown URL; every route takes the organization. */
  public getDetailsUrl(slug: string, organizationId: number): string {
    return `${this.readApiUrl}/details/${slug}/${organizationId}/`;
  }

  /**
   * Record an accept or reopen decision, and get back how many rows it moved.
   *
   * Keyed on `entity_uid`, not the address shown in the table. Three shapes:
   *   '<uid>'  one identity
   *   '*'      every identity currently open on the check ("Accept All")
   *   ''       the check as a whole, which is how tenant-scoped checks are accepted
   *
   * The backend records the actor, the timestamp and the justification in the
   * platform audit log: accepting a risk removes a failing check from the score,
   * so it has to be answerable later.
   */
  public setEntityAcceptance(
    checkId: string,
    organizationId: number,
    entityUid: string,
    accepted: boolean,
    reason: string
  ): Observable<{ ok: boolean; affected: number }> {
    return this.http.post<{ ok: boolean; affected: number }>(
      `${this.liveApiUrl}/posture/risk-checks/${checkId}/affected-users/${organizationId}/`,
      { entity_uid: entityUid, accepted, reason }
    );
  }

  /**
   * Reassign one check's severity tier for this organization.
   *
   * The backend recomputes the score with the new weighting and returns whether the
   * check ends up overridden — choosing the catalog's own tier clears the override
   * rather than storing one. The caller reloads the score afterwards instead of
   * recomputing locally, so the number on screen is the one the backend stands by.
   */
  public setSeverityOverride(
    checkId: string,
    organizationId: number,
    severity: ScoreSeverityTier
  ): Observable<{ ok: boolean; is_overridden: boolean }> {
    return this.http.post<{ ok: boolean; is_overridden: boolean }>(
      `${this.liveApiUrl}/posture/risk-checks/${checkId}/severity/${organizationId}/`,
      { severity }
    );
  }
}
